import React, {useRef, useState} from 'react';
import {useAuth} from '../../contexts/AuthContext';
import {verifyTwoFactorCode, verifyBackupCode} from '../../../components/requests';
import {toast} from 'react-toastify';
import {useLocation, useNavigate} from 'react-router-dom';
import SwitchMethod from "./components/SwitchMethod";

// Type pour les données utilisateurs passés en state dans navigate pour les modifications
interface LocationState {
  id: any
  twoFactorMethod: any
}

const TwoFactorVerifyPage: React.FC = () => {
  const {completeTwoFactor, logout} = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState;
  const [code, setCode] = useState<string[]>(['', '', '', '', '', '']);
  const [backupCodeValue, setBackupCodeValue] = useState('');
  const [useBackup, setUseBackup] = useState(false);
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const isMethodEmail = !!(locationState?.twoFactorMethod && locationState?.twoFactorMethod === "email");
  const PUBLIC_URL = process.env.PUBLIC_URL;

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pastedData.length; i++) {
      newCode[i] = pastedData[i];
    }
    setCode(newCode);
    if (pastedData.length > 0) {
      const focusIndex = Math.min(pastedData.length, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

  const handleVerifyTotp = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) {
      setError('Veuillez saisir un code à 6 chiffres');
      return;
    }

    const maxAttempts = !isMethodEmail ? 5 : 3;
    if (attempts >= maxAttempts) {
      setError('Trop de tentatives. Veuillez vous reconnecter.');
      return;
    }

    setError('');
    setIsVerifying(true);

    try {
      const response = await verifyTwoFactorCode(
        fullCode,
        locationState?.twoFactorMethod ?? null
      );
      if (response.status === 200) {
        await completeTwoFactor(response.data.token);
        navigate('/accueil');
        toast.success('Connexion réussie');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        if (locationState?.twoFactorMethod === "email") {
          if (err?.response?.data?.message === "Expired JWT Token") setError("Code expiré. Veuillez vous reconnecter.");
          if (err?.response?.data?.error === "Expired") setError("Code expiré. Veuillez vous reconnecter.");
          if (err?.response?.data?.error === "Max Attempts") setError("Trop de tentatives. Veuillez vous reconnecter.");
          if (err?.response?.data?.error === "Invalid code") {
            setAttempts(prev => prev + 1);
            setError(`Code invalide. ${3 - attempts - 1} tentative(s) restante(s).`);
          }
        } else {
          setAttempts(prev => prev + 1);
          setError(`Code invalide. ${5 - attempts - 1} tentative(s) restante(s).`);
        }
      } else {
        setError('Une erreur est survenue');
      }
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  const handleVerifyBackup = async () => {
    const trimmedCode = backupCodeValue.trim();
    if (!trimmedCode) {
      setError('Veuillez saisir un code de secours');
      return;
    }

    setError('');
    setIsVerifying(true);

    try {
      const response = await verifyBackupCode(trimmedCode);
      if (response.status === 200) {
        await completeTwoFactor(response.data.token);
        navigate('/accueil');
        toast.success('Connexion réussie');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Code de secours invalide');
      } else {
        setError('Une erreur est survenue');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{background: 'linear-gradient(135deg, #0f172a 0%, #1e3a2f 50%, #14532d 100%)'}}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-emerald-500 rounded-full blur-3xl"/>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"/>
        </div>

        <div className="relative z-10 flex flex-col justify-center px-16 text-white">
          <div className="flex items-center gap-4 mb-12">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-2xl font-bold">
                <img src={`${PUBLIC_URL}/favicon.ico`} alt="logo"/>
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">GROUPE SURPLUS</h1>
              <p className="text-emerald-400 text-sm tracking-widest font-medium">RECYCLAGE</p>
            </div>
          </div>

          <h2 className="text-5xl font-bold mb-6 leading-tight">
            Vérification
            <br/>
            <span className="text-emerald-400">d'identité</span>
          </h2>

          <p className="text-gray-300 text-lg max-w-md mb-10 leading-relaxed">
            Une étape supplémentaire pour protéger votre compte et vos données.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div
        className="flex-1 flex items-center justify-center p-8"
        style={{background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)'}}
      >
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center">
              <span className="text-lg font-bold text-white">
                <img src={`${PUBLIC_URL}/favicon.ico`} alt="logo"/>
              </span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">GROUPE SURPLUS</h1>
              <p className="text-emerald-600 text-xs tracking-widest">RECYCLAGE</p>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-10 shadow-xl border border-gray-100">
            {!useBackup ? (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Code d'authentification</h3>
                <p className="text-gray-500 mb-6 text-sm">
                  Saisissez le code à 6 chiffres
                  {
                    (isMethodEmail)
                      ? ' envoyé par Email sur l\'adresse de réception du code.'
                      : ' affiché dans votre application d\'authentification.'
                  }
                </p>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"/>
                    </svg>
                    {error}
                  </div>
                )}

                <form>
                <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(index, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(index, e)}
                      autoFocus={index === 0}
                      className="w-12 h-14 text-center text-xl font-bold rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all"
                    />
                  ))}
                </div>

                <button
                  type="submit"
                  onClick={handleVerifyTotp}
                  disabled={isVerifying || code.join('').length !== 6 || attempts >= 5}
                  className="w-full py-3.5 rounded-xl text-white font-semibold bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isVerifying ? (
                    <>
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Vérification...
                    </>
                  ) : 'Vérifier'}
                </button>
                </form>

                {(!isMethodEmail) && (
                  <div className="mt-6">
                    <button
                      onClick={() => {
                        setUseBackup(true);
                        setError('');
                      }}
                      className="w-full text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      Utiliser un code de secours
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Code de secours</h3>
                <p className="text-gray-500 mb-6 text-sm">
                  Saisissez l'un de vos codes de secours pour vous connecter.
                </p>

                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex items-center gap-2">
                    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"/>
                    </svg>
                    {error}
                  </div>
                )}

                <form>
                <div className="mb-6">
                  <input
                    type="text"
                    value={backupCodeValue}
                    onChange={(e) => setBackupCodeValue(e.target.value.toUpperCase())}
                    placeholder="Ex: A1B2C3D4"
                    autoFocus
                    className="w-full px-4 py-3.5 rounded-xl border-2 border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all text-gray-900 font-mono text-center text-lg tracking-widest"
                  />
                </div>

                <button
                  type="submit"
                  onClick={handleVerifyBackup}
                  disabled={isVerifying || !backupCodeValue.trim()}
                  className="w-full py-3.5 rounded-xl text-white font-semibold bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 transition-all shadow-lg shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isVerifying ? (
                    <>
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Vérification...
                    </>
                  ) : 'Vérifier le code de secours'}
                </button>
                </form>

                <button
                  onClick={() => {
                    setUseBackup(false);
                    setError('');
                  }}
                  className="w-full mt-4 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
                >
                  Utiliser le code d'authentification
                </button>
              </>
            )}

            <p className="mt-6 text-center text-sm text-gray-500">
              <button onClick={logout} className="text-emerald-600 hover:text-emerald-700 font-medium">
                Se déconnecter
              </button>
            </p>

            {/* Sélecteur de méthode */}
            <SwitchMethod
              id={locationState?.id}
              method={locationState?.twoFactorMethod}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorVerifyPage;
